import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
	memo,
	useCallback,
} from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";
import { PreventLeaveDialog } from "@cayuse/shared-react.components.prevent-leave-page-dialog";
import { isObjectWithKey } from "@cayuse/shared-react.utils.type-checks";
import _ from "lodash";


import FlowableForm from "components/flowable/FlowableForm";
import FormStatusWrapper from "components/FormResponse/FormStatus";
import FormSavingIndicator from "components/FormSaving";
import { FIELD_TYPES } from "globals/constants";
import { useRequestChain } from "hooks";
import {
	loadFormEntity,
	resetForm,
	setFormContentKeys,
	setFormEditLocked,
	setFormEditMode,
	setFormEntity,
	setFormSavingIndicator,
} from "store/actions/form";
import { formSelectors, shellSelectors } from "store/selectors";
import { onOutcomePressed } from "utils";

import FormActions from "./FormActions/FormActions";
import { FormTimeoutModal } from "./FormTimeoutModal";
import Helper from "./helper";
import { useFormCustomFocus } from "./hooks/useFormCustomFocus";
import { useFormImproveA11y } from "./hooks/useFormImproveA11y";
//eslint-disable-next-line import/order
import { useFormSave } from "./hooks/useFormSave";

import "@flowable/forms/flwforms.min.css";
import styles from "./styles.css";

const uploadTypes = [
	FIELD_TYPES.UPLOAD,
	"upload",
	"cayuseResearchTeamBudgetImporter",
];

const FormResponse = ({
	canEdit,
	dictionary,
	form,
	formIdKey,
	formType,
	isFormComplete,
	setIsFormComplete,
	handleFormLoaded,
	justActivatedSectionLabel,
	loadedFormId,
	onFlowableFormSave,
	payloadRootContentKey,
	saveValuesApiUrl,
	saveValuesRecordRevisionApiUrl,
	setIsModificationChange,
	isModificationChange,
	setShouldUpdateForm,
	shouldUpdateForm,
	recordRevisionEditMode,
	disableEditButtons = false,
	onReload,
}) => {
	const {
		error,
		formConfig,
		variables,
		isFetching: isLoading,
		editLock,
		isEditMode: editMode,
	} = useSelector(formSelectors.entity);

	const { userAccountId } = useSelector(shellSelectors.user);

	const { modificationNumber } = useParams();

	const [ isFormReady, setFormReady ] = useState(false);

	const formContainerRef = useRef(null);

	const formId = useMemo(
		() => (isObjectWithKey(form, formIdKey) ? form[formIdKey] : undefined),
		[ form, formIdKey ]
	);

	const formSaving = useRequestChain();

	useFormCustomFocus(isFormReady, formContainerRef);
	useFormImproveA11y(isFormReady, formContainerRef);

	const {
		onEvent,
		onActionTriggered,
		onEditForm,
		dirty,
		saving,
		actionBtnClicked,
	} = useFormSave({
		form,
		formType,
		onReload,
		canEdit,
		isModificationChange,
		onFlowableFormSave,
		formSaving,
		dictionary,
		recordRevisionEditMode,
	});

	useEffect(() => {
		setFormSavingIndicator(formSaving.isFetching);
	}, [ formSaving.isFetching ]);

	useEffect(() => {
		const hasCorrectId = loadedFormId === formId;
		const hasNotFetchedForm =
			_.isEmpty(formConfig) &&
			!error &&
			isObjectWithKey(form, "formTaskId");

		if (
			hasCorrectId &&
			(hasNotFetchedForm || shouldUpdateForm) &&
			!isLoading &&
			form.caseInstanceId
		) {
			setFormEntity(currentEntity => ({
				...currentEntity,
				variables: {},
			}));

			fetchForm();
			setShouldUpdateForm && setShouldUpdateForm(false);
		} else if (!_.isEmpty(formConfig)) {
			setFormReady(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		formConfig,
		loadedFormId,
		formId,
		/**
		 * isLoading is used in case:
		 * - when shouldUpdateForm=true and isLoading=true => re-fetchForm after isLoading=false
		 */
		isLoading,
		shouldUpdateForm,
	]);

	useEffect(() => {
		const lowerCaseFormType = formType.toLowerCase();

		setFormContentKeys({
			payloadRootContentKey: payloadRootContentKey,
			payloadContentType: `${lowerCaseFormType}Content`,
		});
	}, [ formType, payloadRootContentKey ]);

	useEffect(() => {
		return () => resetForm();
	}, []);

	const fetchForm = async () => {
		setFormReady(false);
		setIsModificationChange(false);

		const requestsOptions = {
			update: {
				url: saveValuesApiUrl,
				recordRevisionUrl: saveValuesRecordRevisionApiUrl,
			},
			get: {
				justActivatedSectionLabel,
				uploadTypes,
			},
			modificationNumber
		};

		await loadFormEntity(requestsOptions, form);

		handleFormLoaded(isFormComplete);
		setFormReady(true);
	};

	useEffect(() => {
		/* attachment click handling */
		if ((formContainerRef.current, form?.caseInstanceId)) {
			const formContainerElement = formContainerRef.current;
			if (formContainerElement) {
				const listenerFn = event =>
					Helper.handleClick(event, form.caseInstanceId);

				formContainerElement.addEventListener("click", listenerFn);

				return () =>
					formContainerElement.removeEventListener(
						"click",
						listenerFn
					);
			}
		}
	}, [ form.caseInstanceId, formContainerRef ]);

	const formRefCallback = useCallback(ref => {
		// set ref when form rendered
		Helper.formRef = ref;
	}, []);

	useEffect(() => {
		if (recordRevisionEditMode) {
			//lock the form if other user editing
			if (editLock && editLock.userId && !editMode) {
				const { isNotExpired, isSameUser } = Helper.processEditLock(editLock, userAccountId);

				if (isNotExpired) {
					setFormEditLocked(!isSameUser);
					if (isSameUser && !disableEditButtons) {
						onEditForm();
					}
				}
			} else {
				setFormEditLocked(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ editLock, recordRevisionEditMode, userAccountId, disableEditButtons ]);

	useEffect(() => {
		if(editMode && disableEditButtons) {
			setFormEditMode(false);
		}
	}, [ editMode, disableEditButtons ]);

	return error ? (
		<span className={styles.formError}>
			{dictionary.genericFormError || "Unable to load the form."}
		</span>
	) : (
		<>
			{canEdit && <FormSavingIndicator />}
			{recordRevisionEditMode && (
				<PreventLeaveDialog prevent={editMode} message={dictionary.preventLeaveMessage}/>
			)}
			{recordRevisionEditMode && (
				<>
					{editMode && <FormTimeoutModal onAction={onActionTriggered}/>}
					<FormActions
						editMode={editMode}
						disabled={disableEditButtons}
						saving={saving}
						actionBtnClicked={actionBtnClicked}
						dirty={dirty}
						onAction={onActionTriggered}
					/>
				</>
			)}
			<div
				data-component="FormResponse"
				ref={formContainerRef}
				className={styles.formWrapper}
			>
				<h2 className={styles.formSections}>
					{dictionary.sectionTitle || "Form Sections"}
				</h2>

				{isFormReady ? (
					<>
						<FlowableForm
							formRef={formRefCallback}
							formConfig={formConfig}
							enabled={
								(!recordRevisionEditMode && canEdit) || editMode
							}
							formVariables={variables}
							onEvent={onEvent}
							onOutcomePressed={onOutcomePressed}
						/>

						<FormStatusWrapper
							formSaving={formSaving}
							formType={formType}
							formContainerRef={formContainerRef}
							setIsFormComplete={setIsFormComplete}
						/>
					</>
				) : (
					<LoadingIndicator />
				)}
			</div>
		</>
	);
};

FormResponse.defaultProps = {
	setIsModificationChange: () => {},
};

export default memo(FormResponse);
